'use client';

import Image from 'next/image';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useClientLocale } from '../hooks/useClientLocale';
import { trackClientInfo } from '../services/tracking/trackClientInfo';
import { getListPath } from '../utils/slugUtils';
import { HarlequinBadge } from './HarlequinBadge';
import { TrackableLink } from './TrackableLink';
import { CarouselItem, type Direction } from './ui/carousel';
import { CarouselContainer, type CarouselConfig } from './ui/CarouselContainer';

interface ReadingListItem {
  id: string | number;
  title?: string;
  userId: string | number;
  storiesCount?: number;
  stories?: Array<{
    id: string | number;
    cover?: string;
    cover_url?: string;
    publisher?: string | null;
  }>;
}

interface ReadingListsCarouselProps {
  readingLists: ReadingListItem[];
  userProfiles: Map<string, string>;
  source: string;
  className?: string;
  config?: CarouselConfig;
  fadeColor?: string;
  shelfId?: string;
  shelfWebId?: number;
  shelfTitle?: string;
  theme?: 'light' | 'dark';
  onArrowClick?: (direction: Direction) => void;
  onScrollSnap?: () => void;
  onKeyScroll?: (direction: Direction) => void;
}

const getCover = (stories: ReadingListItem['stories'], index: number): { image: string; publisher: string | null } => {
  const story = stories?.[index];

  return {
    image: story ? story.cover_url || story.cover || '' : '',
    publisher: story?.publisher ?? null,
  };
};

const ReadingListCarouselItem: React.FC<{
  readingList: ReadingListItem;
  authorName: string;
  source: string;
  theme?: 'light' | 'dark';
}> = ({ readingList, authorName, source, theme = 'dark' }) => {
  const { t } = useTranslation();
  const { languageCode } = useClientLocale();
  const listPath = getListPath(readingList.title || '', Number(readingList.id), languageCode);
  const covers: [
    { image: string; publisher: string | null },
    { image: string; publisher: string | null },
    { image: string; publisher: string | null },
  ] = [getCover(readingList.stories, 0), getCover(readingList.stories, 1), getCover(readingList.stories, 2)];

  const bookCount = readingList.storiesCount || readingList.stories?.length || 0;

  return (
    <TrackableLink
      path={listPath}
      className="block group cursor-pointer"
      trackingInfo={{
        name: 'reading-list-carousel-clicked',
        properties: {
          listId: readingList.id,
          listTitle: readingList.title,
          source,
        },
      }}
    >
      <div
        className={`relative flex items-center w-[340px] h-[164px] rounded-xl p-4 pl-[12px] group-hover:scale-[1.02] transition-transform duration-300 overflow-hidden ${
          theme === 'light' ? 'border border-neutral-300 bg-white' : 'border border-white/10 bg-lime/[0.06]'
        }`}
      >
        <div className="max-w-[182px]">
          <h3
            className={`font-semibold text-base leading-6 line-clamp-2 break-words ${
              theme === 'light' ? 'text-neutral-900' : 'text-white'
            }`}
          >
            {readingList.title}
          </h3>
          {authorName && (
            <p
              className={`text-xs line-clamp-1 break-all ${
                theme === 'light' ? 'text-neutral-600' : 'text-neutral-400'
              }`}
            >
              {t('common:by_author', { author: authorName, interpolation: { escapeValue: false } })}
            </p>
          )}
          <div
            className={`inline-flex px-2 py-1 mt-2 rounded-lg backdrop-blur-sm ${
              theme === 'light' ? 'bg-neutral-800' : 'bg-neutral-200'
            }`}
          >
            <span
              className={`text-xs font-semibold leading-[18px] text-center ${
                theme === 'light' ? 'text-white' : 'text-neutral-900'
              }`}
            >
              {t('reading_lists:books_count', { count: bookCount })}
            </span>
          </div>
        </div>
        <div className="absolute right-2 bottom-0 w-[120px] h-[180px]">
          {covers[2].image && (
            <div
              className={`absolute bottom-[-40px] left-1 w-[85px] h-[134px] rounded-lg overflow-hidden z-10 flex-shrink-0 rotate-[15deg] origin-bottom ${
                theme === 'light' ? 'shadow-xl' : 'shadow-[0_4px_32px_0_rgba(0,0,0,0.74)]'
              }`}
            >
              {/* Decorative: the list title is announced via the adjacent <h3> */}
              <Image src={covers[2].image} alt="" fill className="object-cover" sizes="85px" />
              <HarlequinBadge publisher={covers[2].publisher} />
            </div>
          )}
          {covers[1].image && (
            <div
              className={`absolute bottom-[-15px] left-1 w-[74px] h-[115px] rounded-lg overflow-hidden z-20 flex-shrink-0 -rotate-[15deg] origin-bottom ${
                theme === 'light' ? 'shadow-xl' : 'shadow-[0_4px_32px_0_rgba(0,0,0,0.74)]'
              }`}
            >
              <Image src={covers[1].image} alt="" fill className="object-cover" sizes="74px" />
              <HarlequinBadge publisher={covers[1].publisher} />
            </div>
          )}
          {covers[0].image && (
            <div
              className={`absolute bottom-[-10px] left-[13px] w-[85px] h-[134px] rounded-lg overflow-hidden z-30 flex-shrink-0 ${
                theme === 'light' ? 'shadow-xl' : 'shadow-[0_4px_32px_0_rgba(0,0,0,0.74)]'
              }`}
            >
              <Image src={covers[0].image} alt="" fill className="object-cover" sizes="85px" />
              <HarlequinBadge publisher={covers[0].publisher} />
            </div>
          )}
        </div>
      </div>
    </TrackableLink>
  );
};

export const ReadingListsCarousel: React.FC<ReadingListsCarouselProps> = ({
  readingLists,
  userProfiles,
  source,
  className,
  config,
  fadeColor,
  shelfId,
  shelfWebId,
  shelfTitle,
  theme = 'dark',
  onArrowClick,
  onScrollSnap,
  onKeyScroll,
}) => {
  if (!readingLists || readingLists.length === 0) {
    return null;
  }

  const handleArrowClick = (direction: Direction) => {
    if (shelfId && shelfTitle) {
      trackClientInfo('reading-lists-carousel-scroll', {
        shelfId,
        shelfWebId,
        shelfTitle,
        direction,
        scrollType: 'arrow',
        source,
      });
    }
    onArrowClick?.(direction);
  };

  const handleScrollSnap = () => {
    if (shelfId && shelfTitle) {
      trackClientInfo('reading-lists-carousel-scroll', {
        shelfId,
        shelfWebId,
        shelfTitle,
        scrollType: 'swipe',
        source,
      });
    }
    onScrollSnap?.();
  };

  const handleKeyScroll = (direction: Direction) => {
    if (shelfId && shelfTitle) {
      trackClientInfo('reading-lists-carousel-scroll', {
        shelfId,
        shelfWebId,
        shelfTitle,
        direction,
        scrollType: 'keyboard',
        source,
      });
    }
    onKeyScroll?.(direction);
  };

  return (
    <CarouselContainer
      className={className}
      config={config}
      fadeColor={fadeColor}
      navigationClassName={
        theme === 'light' ? '!bg-neutral-900/40 hover:!bg-neutral-900/30' : '!bg-white/40 hover:!bg-white/30'
      }
      onArrowClick={handleArrowClick}
      onScrollSnap={handleScrollSnap}
      onKeyScroll={handleKeyScroll}
      contentClassName="py-2 px-1"
    >
      {readingLists.map((readingList) => {
        const authorName = userProfiles.get(readingList.userId.toString()) || '';

        return (
          <CarouselItem key={readingList.id} className="basis-auto pl-3 md:pl-4">
            <ReadingListCarouselItem readingList={readingList} authorName={authorName} source={source} theme={theme} />
          </CarouselItem>
        );
      })}
    </CarouselContainer>
  );
};

export type { ReadingListItem, ReadingListsCarouselProps };
